'use client';

import { Flex, freddyTheme } from '@coolblue-development/freddy';
import { useTranslation } from '@i18n/useTranslation';
import { InputField } from '@sharedTypes';
import { ReactNode } from 'react';

import { DateHelper } from './DateHelper';
import { FileUploadHelper } from './FileUploadHelper';
import { MatrixHelper } from './MatrixHelper';
import { RadioHelper } from './RadioHelper';
import { TextAreaHelper } from './TextAreaHelper';
import { TextInputHelper } from './TextInputHelper';

type Props = {
  inputFields: InputField[];
  isLoading: boolean;
  locationQuestion?: ReactNode;
  hoursQuestion?: ReactNode;
};

export const InputFields = ({ inputFields, isLoading, locationQuestion, hoursQuestion }: Props) => {
  const { t } = useTranslation('vacancyDetail.applicationForm.questions');

  const DataToInputField = (inputField: InputField) => {
    switch (inputField.type) {
      case 'group':
        return (
          <Flex
            key={inputField.group.toString()}
            wrap='wrap'
            gap={{ xs: 500, md: 800 }}
            sx={{
              '& > *': {
                width: `calc(50% - calc(${freddyTheme.spacing[500]} / 2))`
              },
              [freddyTheme.breakpoint.minWidth('md')]: {
                '& > *': {
                  width: `calc(50% - calc(${freddyTheme.spacing[800]} / 2))`
                }
              }
            }}
          >
            {inputField.group.map(DataToInputField)}
          </Flex>
        );
      case 'radio':
        return (
          <RadioHelper
            key={inputField.id}
            direction={inputField.direction}
            name={inputField.id}
            radioFields={inputField.radioFields}
            label={inputField.label}
            rules={inputField.rules}
            disable={isLoading}
            info={inputField.info}
          />
        );
      case 'textarea':
        return (
          <TextAreaHelper
            key={inputField.id}
            name={inputField.id}
            label={inputField.label}
            placeholder={inputField.placeholder}
            rules={inputField.rules}
            info={inputField.info}
          />
        );
      case 'file':
        return (
          <FileUploadHelper
            key={inputField.id}
            name={inputField.id}
            documentType={inputField.documentType}
            label={inputField.label}
            buttonText={inputField.title}
            rules={inputField.rules}
            info={inputField.info}
          />
        );
      case 'matrix':
        return (
          <MatrixHelper
            key={inputField.id}
            name={inputField.id}
            label={inputField.label}
            info={inputField.info}
            rules={inputField.rules}
            columnLabels={[
              t('availabilityMatrix.monday'),
              t('availabilityMatrix.tuesday'),
              t('availabilityMatrix.wednesday'),
              t('availabilityMatrix.thursday'),
              t('availabilityMatrix.friday'),
              t('availabilityMatrix.saturday'),
              t('availabilityMatrix.sunday')
            ]}
            rowLabels={[
              t('availabilityMatrix.wholeDay'),
              t('availabilityMatrix.morning'),
              t('availabilityMatrix.afternoon'),
              t('availabilityMatrix.evening')
            ]}
            firstRowSelectsAll
          />
        );
      case 'date':
        return (
          <DateHelper
            key={inputField.id}
            name={inputField.id}
            label={inputField.label}
            rules={inputField.rules}
            disable={isLoading}
            info={inputField.info}
          />
        );
      default:
        return (
          <TextInputHelper
            key={inputField.id}
            name={inputField.id}
            type={inputField.type}
            label={inputField.label}
            placeholder={inputField.placeholder}
            disable={isLoading}
            rules={inputField.rules}
            info={inputField.info}
          />
        );
    }
  };

  const lastDefaultQuestionIndex =
    inputFields.findLastIndex(input => input.questionType === 'default') + 1;

  return (
    <Flex
      direction='column'
      gap={{ xs: 400, md: `${freddyTheme.spacing[500]} ${freddyTheme.spacing[800]}` }}
      sx={{
        [freddyTheme.breakpoint.minWidth('md')]: {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(40%, 1fr))',
          gridAutoFlow: 'column',
          overflow: 'hidden',
          '& > :nth-of-type(1)': {
            gridColumn: '1 / span 2'
          },
          '& > :nth-of-type(2n)': {
            gridColumn: '1'
          }
        }
      }}
    >
      {inputFields.slice(0, lastDefaultQuestionIndex).map(DataToInputField)}
      {locationQuestion}
      {inputFields.slice(lastDefaultQuestionIndex).map(DataToInputField)}
      {hoursQuestion}
    </Flex>
  );
};
